const invalidControlSelector = [
    'input[aria-invalid="true"]',
    'select[aria-invalid="true"]',
    'textarea[aria-invalid="true"]',
    '[tabindex][aria-invalid="true"]',
    '.wpcf7-not-valid input:not([type="hidden"])',
    '.wpcf7-not-valid select',
    '.wpcf7-not-valid textarea',
].join( ', ' );

const cf7UpdateWaits = new WeakMap();
const errorDescriptions = new WeakMap();

function addErrorIcon( tip ) {
    if ( tip.querySelector( '.cf7-error-icon' ) ) {
        return;
    }

    const icon = document.createElementNS( 'http://www.w3.org/2000/svg', 'svg' );
    icon.classList.add( 'cf7-error-icon' );
    icon.setAttribute( 'viewBox', '0 0 20 20' );
    icon.setAttribute( 'aria-hidden', 'true' );
    icon.setAttribute( 'focusable', 'false' );
    const path = document.createElementNS( icon.namespaceURI, 'path' );
    path.setAttribute( 'd', 'M10 2L19 18H1Z M10 7V12 M10 14V15' );
    path.setAttribute( 'fill', 'none' );
    path.setAttribute( 'stroke', 'currentColor' );
    path.setAttribute( 'stroke-width', '1.5' );
    icon.append( path );
    tip.prepend( icon );
}

function clearErrorDescriptions( form ) {
    ( errorDescriptions.get( form ) || [] ).forEach( ( { control, errorId } ) => {
        const ids = ( control.getAttribute( 'aria-describedby' ) || '' ).split( /\s+/ )
            .filter( ( id ) => id && id !== errorId );
        if ( ids.length ) {
            control.setAttribute( 'aria-describedby', ids.join( ' ' ) );
        } else {
            control.removeAttribute( 'aria-describedby' );
        }
    } );
    errorDescriptions.delete( form );
}

function idPart( value ) {
    return String( value || '' ).replace( /[^A-Za-z0-9_-]/g, '-' );
}

function getErrorId( form, fieldName, index ) {
    const formId = form.closest( '.wpcf7' )?.id || form.getAttribute( 'data-wpcf7-id' ) || 'form';

    return `cf7-error-${ idPart( formId ) }-${ idPart( fieldName ) }-${ index }`;
}

function getFieldLabel( form, control, wrapper ) {
    const controlId = control.id;
    const explicitLabel = controlId && Array.from( control.labels || [] )
        .find( ( label ) => label.htmlFor === controlId );
    const label = explicitLabel || control.closest( 'label' );
    const labelCopy = label?.cloneNode( true );

    labelCopy?.querySelectorAll( 'input, select, textarea, .wpcf7-not-valid-tip, [role="alert"], [aria-live]' ).forEach( ( message ) => {
        message.remove();
    } );

    const labelText = labelCopy?.textContent?.trim();

    return labelText || control.getAttribute( 'aria-label' ) || wrapper.dataset.name || '';
}

function setDescribedBy( control, errorId ) {
    const ids = ( control.getAttribute( 'aria-describedby' ) || '' )
        .split( /\s+/ )
        .filter( Boolean )
        // CF7's generated screen-reader-list item is transient and is not the
        // message displayed next to the control.
        .filter( ( id ) => !id.includes( '-ve-' ) );

    if ( !ids.includes( errorId ) ) {
        ids.push( errorId );
    }

    control.setAttribute( 'aria-describedby', ids.join( ' ' ) );
}

function makeFieldErrorsAccessible( form ) {
    const errors = [];
    clearErrorDescriptions( form );
    const descriptions = [];

    form.querySelectorAll( '.wpcf7-form-control-wrap' ).forEach( ( wrapper, index ) => {
        const tip = wrapper.querySelector( '.wpcf7-not-valid-tip' );
        const controls = Array.from( wrapper.querySelectorAll( 'input:not([type="hidden"]), select, textarea, [tabindex]' ) )
            .filter( ( control ) => control.matches( invalidControlSelector ) || wrapper.querySelector( '.wpcf7-not-valid' ) );
        const control = controls[0];

        if ( !tip || !control ) {
            return;
        }

        const errorId = getErrorId( form, wrapper.dataset.name, index );
        const message = tip.textContent.trim();

        tip.id = errorId;
        // A message referenced by aria-describedby must not be hidden from the
        // accessibility tree. The form-level status below avoids alert noise.
        tip.setAttribute( 'aria-hidden', 'false' );
        addErrorIcon( tip );
        controls.forEach( ( field ) => {
            setDescribedBy( field, errorId );
            descriptions.push( { control: field, errorId } );
        } );

        const label = getFieldLabel( form, control, wrapper );
        if ( message ) {
            errors.push( label ? `${ label }: ${ message }` : message );
        }
    } );

    errorDescriptions.set( form, descriptions );
    return errors;
}

function getStatusRegion( form ) {
    const root = form.closest( '.wpcf7' );
    let status = root?.querySelector( '.screen-reader-response [role="status"]' );

    if ( status || !root ) {
        return status;
    }

    status = document.createElement( 'div' );
    status.className = 'visually-hidden cf7-accessibility-status';
    status.setAttribute( 'role', 'status' );
    root.prepend( status );

    return status;
}

function isFocusable( element ) {
    return !element.disabled && element.getClientRects().length > 0;
}

function focusFirstInvalidControl( form ) {
    const control = Array.from( form.querySelectorAll( invalidControlSelector ) )
        .find( isFocusable );

    if ( control && document.activeElement !== control ) {
        control.focus();
    }
}

function synchronizeInvalidForm( form, moveFocus ) {
    const errors = makeFieldErrorsAccessible( form );
    const status = getStatusRegion( form );

    if ( status ) {
        status.setAttribute( 'aria-live', 'polite' );
        status.setAttribute( 'aria-atomic', 'true' );
        status.textContent = errors.join( '. ' );
    }

    if ( moveFocus && errors.length ) {
        focusFirstInvalidControl( form );
    }

    return errors.length > 0;
}

function waitForCf7Updates( form ) {
    cf7UpdateWaits.get( form )?.();

    const root = form.closest( '.wpcf7' ) || form;
    const retryTimers = [];
    let timer;
    let observer;
    let stopped = false;
    let focused = false;

    const stop = () => {
        stopped = true;
        window.clearTimeout( timer );
        retryTimers.forEach( window.clearTimeout );
        observer?.disconnect();

        if ( cf7UpdateWaits.get( form ) === stop ) {
            cf7UpdateWaits.delete( form );
        }
    };

    const synchronize = () => {
        if ( stopped ) {
            return;
        }

        // Do not react to the DOM changes made by synchronizeInvalidForm itself.
        observer.disconnect();
        focused = synchronizeInvalidForm( form, !focused ) || focused;
        observer.observe( root, { childList: true, subtree: true, characterData: true } );
    };

    const scheduleSynchronization = () => {
        if ( stopped ) {
            return;
        }

        window.clearTimeout( timer );
        timer = window.setTimeout( synchronize, 100 );
    };

    // CF7 can make its live-region and validation changes in a later animation
    // frame. Observe those changes, then wait for its DOM to become quiet.
    observer = new MutationObserver( scheduleSynchronization );
    observer.observe( root, { childList: true, subtree: true, characterData: true } );
    cf7UpdateWaits.set( form, stop );
    scheduleSynchronization();

    // Keep fallbacks for CF7 versions/extensions that defer an update without
    // producing an observable mutation in the form subtree.
    retryTimers.push( window.setTimeout( synchronize, 250 ) );
    retryTimers.push( window.setTimeout( synchronize, 500 ) );
    retryTimers.push( window.setTimeout( stop, 750 ) );
}

export default function initializeCf7Accessibility() {
    const clearForm = ( event ) => {
        const form = event.target.matches( 'form' ) ? event.target : event.target.querySelector( 'form' );
        if ( !form ) {
            return;
        }
        cf7UpdateWaits.get( form )?.();
        clearErrorDescriptions( form );
        const status = getStatusRegion( form );
        if ( status ) {
            status.textContent = '';
        }
    };

    document.addEventListener( 'wpcf7beforesubmit', clearForm );
    document.addEventListener( 'wpcf7reset', clearForm );

    document.addEventListener( 'wpcf7invalid', ( event ) => {
        const form = event.target.matches( 'form' ) ? event.target : event.target.querySelector( 'form' );

        if ( !form ) {
            return;
        }

        waitForCf7Updates( form );
    } );
}
